import { getPreset } from '@cutr/constants/windows/presets';
import { PresetId } from '@cutr/constants/windows/presets';
import {
  AreaId,
  ConfigurationValues,
  Position,
  SashbarConfiguration,
  WindowSpec,
} from '@cutr/constants/windows/windows';
import { SashType } from '@cutr/constants/windows/windows-configurations.ts';
import {
  NewAreaDimension,
  NewFrameDimension,
} from '@cutr/constants/windows/windows-presets.ts';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { hashObject, pick } from '@/utils/misc';

const SAVE_SENSITIVE_FIELDS: (keyof Position)[] = [
  'title',
  'quantity',
  'windowSpec',
  'configurations',
  'presetId',
];

export type PositionState = Position & {
  selectedAreaId?: AreaId;
  hash: number;
};

const DEFAULT_STORE: PositionState = {
  title: '',
  quantity: 1,
  windowSpec: undefined,
  configurations: {},
  presetId: 'none',

  id: '',
  createdAt: '',
  updatedAt: '',
  archivedAt: '',
  windowOrderId: '',
  hash: 0,
};

export type PositionStoreActions = {
  init: (position: Position) => void;

  setTitle: (title: string) => void;
  setQuantity: (quantity: number) => void;
  /**
   * @deprecated this should only be used in a ConfigurationsProvider's onConfigurationChange
   */
  setConfigurations: (configurations: Partial<ConfigurationValues>) => void;
  setPresetId: (presetId: PresetId) => void;
  setWindowSpec: (windowSpec: WindowSpec) => void;

  resize: (event: NewFrameDimension | NewAreaDimension) => void;

  toggleSelectedAreaId: (areaId: AreaId) => void;
  setAreaSashType: (areaId: AreaId, sashType: SashType) => void;
  setAreaSashbars: (
    areaId: AreaId,
    sashbars: SashbarConfiguration[] | undefined
  ) => void;
  setHash: (hash: number) => void;
};

export const usePositionStore = create<PositionState & PositionStoreActions>()(
  devtools(
    (set) => {
      return {
        ...DEFAULT_STORE,
        setTitle: (title) => set(() => ({ title })),
        setQuantity: (quantity) => set(() => ({ quantity })),
        setConfigurations: (configurations) => set(() => ({ configurations })),
        setPresetId: (presetId) => set(() => ({ presetId })),
        setWindowSpec: (windowSpec) => set(() => ({ windowSpec })),
        init: (position) =>
          set((state) => {
            return {
              ...DEFAULT_STORE,
              ...position,
              selectedAreaId:
                state.id === position.id ? state.selectedAreaId : undefined,
              hash: hashPosition(position),
            };
          }),
        resize: (event) =>
          set((state) => {
            const { presetId, windowSpec } = state;
            if (!presetId || presetId === 'none' || !windowSpec) {
              return state;
            }

            const preset = getPreset(presetId);
            const presetState = windowSpec.meta.presetState;

            const newPresetState =
              event.target === 'frame'
                ? preset.resizeFrame(presetState, event)
                : preset.resizeArea(presetState, event);

            return {
              windowSpec: {
                ...windowSpec,
                ...preset.layout(newPresetState),
                meta: {
                  ...windowSpec.meta,
                  presetState: newPresetState,
                },
              },
            };
          }),
        toggleSelectedAreaId: (areaId) =>
          set((state) => ({
            selectedAreaId:
              state.selectedAreaId === areaId ? undefined : areaId,
          })),
        setAreaSashType: (areaId, sashType) =>
          set((state) => {
            if (!state.windowSpec || !areaId) {
              return state;
            }

            const windowSpec = state.windowSpec;
            const areaConfiguration =
              windowSpec.areaConfigurations[areaId] || {};

            return {
              windowSpec: {
                ...windowSpec,
                areaConfigurations: {
                  ...windowSpec.areaConfigurations,
                  [areaId]: {
                    ...areaConfiguration,
                    sashType,
                  },
                },
              },
            };
          }),
        setAreaSashbars: (areaId, sashbars) =>
          set((state) => {
            if (!state.windowSpec || !areaId) {
              return state;
            }

            const windowSpec = state.windowSpec;
            const areaConfiguration = windowSpec.areaConfigurations[areaId];

            if (!areaConfiguration) {
              return state;
            }

            const nextAreaConfiguration = { ...areaConfiguration };
            if (!sashbars) {
              delete nextAreaConfiguration.sashbars;
            } else {
              nextAreaConfiguration.sashbars = sashbars;
            }

            return {
              windowSpec: {
                ...windowSpec,
                areaConfigurations: {
                  ...windowSpec.areaConfigurations,
                  [areaId]: nextAreaConfiguration,
                },
              },
            };
          }),
        setHash: (hash) => set(() => ({ hash })),
      };
    },
    { name: 'position-store' }
  )
);

export const hashPosition = (position: Position | PositionState) => {
  return hashObject(pick(position, SAVE_SENSITIVE_FIELDS));
};
